
import React, { useState } from 'react';
import { Home, Calendar, ArrowRight, Sparkles, ChevronLeft, Calculator, Layers, AlertCircle, Lock } from 'lucide-react';
import { BudgetMode, PlanTier } from '../types';

interface ProjectSetupProps {
  onNext: (data: { name: string; initialBudget: number; startDate: string; budgetMode: BudgetMode; logoUrl?: string }) => void;
  onBack: () => void;
  planTier?: PlanTier;
  onUpgrade?: () => void;
}

export const ProjectSetup: React.FC<ProjectSetupProps> = ({ onNext, onBack, planTier = 'trial', onUpgrade }) => {
  const [formData, setFormData] = useState({
    name: '',
    budget: '',
    startDate: new Date().toISOString().split('T')[0],
    budgetMode: 'general' as BudgetMode
  });
  
  const [error, setError] = useState<string | null>(null);

  const isPro = planTier === 'pro_plus';

  const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, "");
    const cleanValue = rawValue ? parseInt(rawValue).toString() : "";
    setFormData({ ...formData, budget: cleanValue });
    if (error) setError(null);
  };

  const getDisplayValue = () => {
    if (!formData.budget) return "";
    const val = parseInt(formData.budget);
    return isNaN(val) ? "" : val.toLocaleString('pt-BR');
  };

  const handleModeChange = (mode: BudgetMode) => {
    if (mode === 'detailed' && !isPro) {
      if (onUpgrade) onUpgrade();
      return;
    }
    setFormData({ ...formData, budgetMode: mode });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError("Por favor, dê um nome para sua obra.");
      return;
    }
    
    let budgetInt = 0;
    if (formData.budgetMode === 'general') {
      budgetInt = parseInt(formData.budget);
      if (isNaN(budgetInt) || budgetInt <= 0) {
        setError("Por favor, insira um valor de orçamento válido maior que zero.");
        return;
      }
    }

    onNext({
      name: formData.name,
      initialBudget: budgetInt,
      startDate: formData.startDate,
      budgetMode: formData.budgetMode,
      logoUrl: undefined
    });
  };

  const inputClasses = "w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm rounded-2xl py-4 pl-12 pr-4 focus:ring-4 focus:ring-emerald-100 dark:focus:ring-emerald-800/30 transition-all outline-none font-semibold text-emerald-900 dark:text-white text-sm";
  const labelClasses = "text-[10px] uppercase font-black text-slate-500 dark:text-slate-400 tracking-widest block mb-3 ml-1";

  return (
    <div className="min-h-screen mesh-gradient flex flex-col px-8 pt-12 pb-10 animate-fadeIn overflow-y-auto">
      <div className="mb-12 flex items-center gap-4">
        <button onClick={onBack} className="p-2 bg-white dark:bg-slate-900 rounded-full shadow-sm hover:bg-slate-50 transition-colors">
          <ChevronLeft size={20} className="text-slate-500" />
        </button>
        <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 w-1/4 rounded-full transition-all duration-700 shadow-[0_0_10px_rgba(16,185,129,0.3)]"></div>
        </div>
        <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">1 / 4</span>
      </div>

      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 dark:bg-emerald-800/30 rounded-full text-[9px] font-black text-emerald-800 dark:text-emerald-400 uppercase tracking-widest mb-4">
          <Sparkles size={10} /> Primeiros passos
        </div>
        <h1 className="text-3xl font-black text-emerald-900 dark:text-white tracking-tighter leading-[1.1] mb-3">
          Sua obra começa com <span className="text-emerald-600">bons fundamentos</span>
        </h1>
        <p className="text-slate-600 dark:text-slate-400 text-sm font-medium leading-relaxed">
          Defina como você deseja controlar o dinheiro da sua obra.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-7 flex-1">
        <div>
          <label className={labelClasses}>Nome da Obra</label>
          <div className="relative">
            <Home className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-800/60" size={18} />
            <input 
              type="text" required placeholder="Ex: Casa da Lagoa" 
              className={inputClasses}
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
            />
          </div>
        </div>

        <div>
           <label className={labelClasses}>Como definir o Orçamento?</label>
           <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                type="button"
                onClick={() => handleModeChange('general')}
                className={`p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all ${formData.budgetMode === 'general' ? 'bg-emerald-50 border-emerald-500 shadow-md scale-[1.02]' : 'bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-700 opacity-70'}`}
              >
                 <Calculator size={20} className={formData.budgetMode === 'general' ? 'text-emerald-600' : 'text-slate-400'} />
                 <span className={`text-[9px] font-black uppercase tracking-widest ${formData.budgetMode === 'general' ? 'text-emerald-900' : 'text-slate-400'}`}>Valor Geral</span>
              </button>
              <button
                type="button"
                onClick={() => handleModeChange('detailed')}
                className={`p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all relative ${formData.budgetMode === 'detailed' ? 'bg-emerald-50 border-emerald-500 shadow-md scale-[1.02]' : 'bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-700 opacity-70'}`}
              >
                 {!isPro && <Lock size={12} className="absolute top-2 right-2 text-slate-400" />}
                 <Layers size={20} className={formData.budgetMode === 'detailed' ? 'text-emerald-600' : 'text-slate-400'} />
                 <span className={`text-[9px] font-black uppercase tracking-widest ${formData.budgetMode === 'detailed' ? 'text-emerald-900' : 'text-slate-400'}`}>Por Etapas</span>
              </button>
           </div>

           {formData.budgetMode === 'general' ? (
              <div className="relative animate-fadeIn">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 font-black text-emerald-800/60 text-xs">R$</div>
                <input 
                  type="text" 
                  inputMode="numeric"
                  placeholder="0" 
                  className={inputClasses}
                  value={getDisplayValue()}
                  onChange={handleBudgetChange}
                />
              </div>
           ) : (
             <div className="bg-emerald-50 dark:bg-emerald-900/10 p-4 rounded-2xl border border-emerald-100 dark:border-emerald-900/30 animate-fadeIn">
                <p className="text-xs font-bold text-emerald-800 dark:text-emerald-400 leading-relaxed text-center">
                   Você definirá o valor de cada etapa na próxima tela. O total será a soma de tudo.
                </p>
             </div>
           )}
        </div>

        <div>
          <label className={labelClasses}>Data de Início</label>
          <div className="relative">
            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-800/60" size={18} />
            <input 
              type="date" required 
              className={inputClasses}
              value={formData.startDate}
              onChange={e => setFormData({...formData, startDate: e.target.value})}
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-xl flex items-center gap-3 text-xs font-bold animate-shake border border-red-100 dark:border-red-900/30">
            <AlertCircle size={18} />
            {error}
          </div>
        )}

        <div className="pt-2 pb-6">
          <button 
            type="submit"
            className="w-full bg-emerald-800 dark:bg-emerald-600 text-white py-5 rounded-[2rem] font-black text-xs uppercase tracking-widest shadow-2xl flex items-center justify-center gap-3 active:scale-95 transition-all hover:bg-emerald-700"
          >
            Próximo Passo
            <ArrowRight size={18} />
          </button>
        </div>
      </form>
    </div>
  );
};
